import React, { forwardRef, HTMLAttributes } from "react";

import { textRecipe } from "./text.css";

/**
 * Props for the Text component
 */
export type TextProps = {
  /** The typography variant to use */
  variant?:
    | "heading1"
    | "heading2"
    | "heading2h"
    | "heading3"
    | "headingL"
    | "headingM"
    | "headingS"
    | "bodyXL"
    | "bodyL"
    | "bodyM"
    | "bodyS"
    | "bodyXS"
    | "bodyXXS"
    | "body";
  /** The font weight to apply */
  weight?: "normal" | "bold" | "heavy" | "extrabold";
  /** The text color to use */
  color?:
    | "link-base"
    | "monetize-base"
    | "info-base"
    | "positive-base"
    | "error-base"
    | "warning-base"
    | "special-base"
    | "neutral-mostSubdue"
    | "neutral-moreSubdue"
    | "neutral-subdue"
    | "neutral-base"
    | "neutral-highlight"
    | "neutral-moreHighlight"
    | "inverted-moreSubdue"
    | "inverted-subdue"
    | "inverted-base";
  /** The line height to use */
  lineHeight?:
    | "bodyXXS"
    | "bodyXS"
    | "bodyS"
    | "body"
    | "bodyM"
    | "bodyL"
    | "bodyXL"
    | "headingS"
    | "headingM"
    | "headingL"
    | "heading3"
    | "heading2"
    | "heading2h"
    | "heading1";
} & HTMLAttributes<HTMLSpanElement>;

/**
 * Intentionally simple Text component, which enforces variants through VE's recipe system.
 * @example
 * ```tsx
 * <Text variant="heading1">Large Heading</Text>
 * <Text variant="body" color="neutral-base">Regular body text</Text>
 * ```
 */
export const Text = forwardRef<HTMLSpanElement, TextProps>((props: TextProps, ref) => {
  const { variant, weight, color, lineHeight, ...spanProps } = props;
  const variantClass = textRecipe({ variant, weight, color, lineHeight });

  return <span className={variantClass} {...spanProps} ref={ref} />;
});

Text.displayName = "Text";
